<template>
  <div class="vehicle-live">
    <div id="vehicleMap"></div>

    <!-- 顶栏（桌面单行；手机两行：标题+返回 / 考试+状态） -->
    <div class="vl-topbar">
      <div class="vl-row row-title">
        <div class="vl-title">
          <h1 class="vl-title-main">{{ pageTitle }}</h1>
        </div>
        <div
          class="vl-cam-toggle"
          v-if="hasAnyCamera"
          :title="videoOn ? '关闭摄像头' : '打开摄像头'"
          @click="toggleVideo"
        >📹</div>
        <div class="vl-back" @click="goBack">返回上一页</div>
      </div>
      <div class="vl-row row-data">
        <!-- 桌面:可搜索下拉框;移动端:pill 按钮唤起底部弹层(弹层内可搜索) -->
        <template v-if="!isMobile">
          <el-select
            v-model="examId"
            size="small"
            clearable
            filterable
            placeholder="全部车辆（不限考试）"
            class="vl-exam-select"
            @change="onExamChange"
          >
            <el-option v-for="e in examOptions" :key="e.id" :label="e.examName" :value="e.id" />
          </el-select>
          <el-select
            v-model="deptFilter"
            size="small"
            clearable
            filterable
            placeholder="全部分组（区）"
            class="vl-exam-select"
          >
            <el-option v-for="d in deptOrder" :key="d" :label="d" :value="d" />
          </el-select>
        </template>
        <template v-else>
          <div class="vl-pill" @click="openSheet('exam')">
            {{ examId ? currentExamName : "全部考试" }} ▾
          </div>
          <div class="vl-pill" @click="openSheet('dept')">{{ deptFilter || "全部分组" }} ▾</div>
        </template>
        <div class="vl-status">
          <span :class="['live-dot', fetching ? 'busy' : '']"></span>
          <template v-if="lastUpdate">更新于 {{ lastUpdate }}</template>
          <template v-else>连接中…</template>
        </div>
      </div>
    </div>

    <!-- 日/夜切换（角落小按钮，不明显） -->
    <div
      class="vl-mode-btn"
      :title="nightMode ? '切换白天模式' : '切换夜间模式'"
      @click="toggleMode"
    >{{ nightMode ? "☀️" : "🌙" }}</div>

    <!-- 试卷车列表按钮（仅移动端，叠在月亮按钮上方；桌面用左侧面板） -->
    <div class="vl-cars-btn" title="试卷车列表" @click="panelOpen = !panelOpen">🚗</div>

    <!-- 左侧车辆列表（手机/平板为底部抽屉）：北京市设备多，按区/账号分组，可整体展开/收起 -->
    <div :class="['vl-panel', { sheet: isMobile, open: panelOpen }]">
      <div class="vl-panel-head" @click="onPanelHead">
        试卷车<span class="vl-count">（在线 {{ onlineCount }} / {{ vehicles.length }} 台）</span>
        <span :class="['vl-arrow', { open: panelOpen }]">▼</span>
      </div>
      <div class="vl-panel-tools">
        <span class="vl-tool" @click="expandAllGroups">展开全部</span>
        <span class="vl-tool" @click="collapseAllGroups">收起全部</span>
      </div>
      <div class="vl-cards">
        <div v-for="g in vehicleGroups" :key="g.name" class="vl-group">
          <div class="vl-group-head" @click="toggleGroup(g)">
            <span :class="['vl-arrow-sm', { open: isGroupOpen(g) }]">▶</span>
            <span class="vl-group-name" :title="g.name">{{ g.name }}</span>
            <span class="vl-group-count">在线 {{ g.onlineCount }}/{{ g.list.length }}</span>
          </div>
          <template v-if="isGroupOpen(g)">
            <div
              v-for="v in g.list"
              :key="v.deviceIMEI"
              :class="['vl-card', { active: v.deviceIMEI === followImei, offline: !v.online }]"
              @click="followVehicle(v)"
            >
              <div class="vl-card-top">
                <span :class="['dot', v.online ? 'on' : 'off']"></span>
                <span class="name" :title="v.deviceName || v.deviceIMEI">{{ v.deviceName || v.deviceIMEI }}</span>
                <span :class="['speed', { moving: v.online && v.speed > 1 }]">
                  {{ v.online ? (Number(v.speed) || 0).toFixed(1) + " km/h" : "离线" }}
                </span>
              </div>
              <div class="vl-card-sub">
                定位 {{ v.locationTime || "--" }}<template v-if="v.electric != null"> · GPS电量 {{ Math.round(v.electric) }}%</template>
              </div>
              <div class="vl-card-sub" v-if="serialMap[v.deviceIMEI]">
                摄像头ID {{ serialMap[v.deviceIMEI] }} · 摄像头电量 {{ camBattery[v.deviceIMEI] != null ? camBattery[v.deviceIMEI] + "%" : "--" }}
              </div>
            </div>
          </template>
        </div>
        <div v-if="!vehicles.length" class="vl-empty">
          {{ loading ? "数据加载中…" : "暂无车辆数据" }}
        </div>
      </div>
      <div class="vl-panel-foot">
        <el-checkbox v-model="showTrail" size="small">跟随车辆时显示今日轨迹</el-checkbox>
        <el-button size="mini" type="text" class="vl-fitview" @click="fitAll">全视野</el-button>
      </div>
    </div>

    <!-- PC:右侧竖排摄像头(最多3路),顶栏📹开关控制;摄像头车超过3台时每路可下拉换车,点画面全屏 -->
    <div class="vl-videos" v-if="!isMobile && pcVideoOn && liveSlots.length">
      <div v-for="(slot, idx) in liveSlots" :key="'pc-' + slot.imei" class="vl-video-card">
        <div class="vl-video-head">
          <el-select
            v-if="canPickCams"
            class="vl-cam-pick"
            popper-class="vl-cam-pick-popper"
            :value="slot.imei"
            size="mini"
            @change="(imei) => onPickSlot(idx, imei)"
          >
            <el-option
              v-for="c in camCandidates"
              :key="c.imei"
              :label="flatName(c.name)"
              :value="c.imei"
              :disabled="liveSlots.some((s, i) => i !== idx && s.imei === c.imei)"
            />
          </el-select>
          <span v-else class="name" :title="slot.name">{{ slot.name }}</span>
          <span :class="['st', slot.ready ? 'on' : 'off']">{{ slot.ready ? "在线" : "不可用" }}</span>
        </div>
        <div
          :class="['vl-video-box', { clickable: slot.ready }]"
          :title="slot.ready ? '点击全屏观看' : ''"
          @click="slot.ready && openFullscreen(slot.imei)"
        >
          <div :id="'vlLive-' + slot.imei" class="vl-player"></div>
          <div v-if="!slot.ready" class="vl-video-off">
            <span>{{ slot.err }}</span>
            <i v-if="slot.hint">{{ slot.hint }}</i>
          </div>
          <div v-if="slot.ready" class="vl-video-fullhint">⛶ 全屏</div>
        </div>
      </div>
    </div>

    <!-- 移动端:单路摄像头面板(◀▶切换适合车少时;点标题弹层选车,车多时用) -->
    <div class="vl-video-single" v-if="isMobile && mobileVideoOn && mobileSlot">
      <div class="vl-video-head">
        <span class="vl-video-switch" @click="switchMobileCam(-1)">◀</span>
        <span class="name pick" :title="mobileSlot.name" @click="videoPickerOpen = true">
          {{ mobileSlot.name }} <span class="pick-mark">▾</span>
        </span>
        <span class="vl-video-switch" @click="switchMobileCam(1)">▶</span>
      </div>
      <div class="vl-video-box">
        <div :id="'vlLive-' + mobileSlot.imei" class="vl-player"></div>
        <div v-if="!mobileSlot.ready" class="vl-video-off">
          <span>{{ mobileSlot.err }}</span>
          <i v-if="mobileSlot.hint">{{ mobileSlot.hint }}</i>
        </div>
      </div>
      <div v-if="isWeChat" class="vl-video-wechat">微信内可能无法播放，建议菜单「在浏览器打开」</div>
    </div>

    <!-- PC:全屏观看一路(常驻关闭按钮,Esc 也可关闭) -->
    <div class="vl-fs" v-if="fsSlot">
      <div class="vl-fs-head">
        <span class="name" :title="fsSlot.name">{{ flatName(fsSlot.name) }}</span>
        <span class="st on">直播中</span>
        <div class="vl-fs-close" title="关闭全屏（Esc）" @click="closeFullscreen">✕ 关闭全屏</div>
      </div>
      <div class="vl-fs-box">
        <div id="vlLive-fs" class="vl-player"></div>
      </div>
    </div>

    <!-- 移动端:筛选底部弹层(考试/分组),弹层内可输关键字过滤选项 -->
    <div v-if="sheetType" class="vl-sheet-mask" @click="sheetType = ''">
      <div class="vl-sheet" @click.stop>
        <div class="vl-sheet-head">
          <span>{{ sheetType === "exam" ? "按考试筛选" : "按分组（区）筛选" }}</span>
          <span class="vl-sheet-close" @click="sheetType = ''">✕</span>
        </div>
        <div class="vl-sheet-search">
          <input v-model="sheetKey" :placeholder="sheetType === 'exam' ? '输入考试名称过滤' : '输入区名过滤'" />
          <span v-if="sheetKey" class="vl-sheet-clear" @click="sheetKey = ''">清空</span>
        </div>
        <div class="vl-sheet-list">
          <template v-if="sheetType === 'exam'">
            <div class="vl-sheet-item" :class="{ active: !examId }" @click="onPickExam(null)">全部车辆（不限考试）</div>
            <div
              v-for="e in sheetExamOptions"
              :key="e.id"
              class="vl-sheet-item"
              :class="{ active: examId === e.id }"
              @click="onPickExam(e.id)"
            >{{ e.examName }}</div>
            <div v-if="!sheetExamOptions.length" class="vl-sheet-empty">无匹配项</div>
          </template>
          <template v-else>
            <div class="vl-sheet-item" :class="{ active: !deptFilter }" @click="onPickDept('')">全部分组</div>
            <div
              v-for="d in sheetDeptOptions"
              :key="d"
              class="vl-sheet-item"
              :class="{ active: deptFilter === d }"
              @click="onPickDept(d)"
            >{{ d }}</div>
            <div v-if="!sheetDeptOptions.length" class="vl-sheet-empty">无匹配项</div>
          </template>
        </div>
      </div>
    </div>

    <!-- 移动端:摄像头选车弹层(车多时左右切换太慢,点标题直接选) -->
    <div v-if="videoPickerOpen" class="vl-sheet-mask" @click="videoPickerOpen = false">
      <div class="vl-sheet" @click.stop>
        <div class="vl-sheet-head">
          <span>选择摄像头车辆</span>
          <span class="vl-sheet-close" @click="videoPickerOpen = false">✕</span>
        </div>
        <div class="vl-sheet-search">
          <input v-model="videoPickerKey" placeholder="搜索车牌 / IMEI" />
        </div>
        <div class="vl-sheet-list">
          <div
            v-for="c in videoPickList"
            :key="c.imei"
            class="vl-sheet-item"
            :class="{ active: (mobileLiveImei || (mobileSlot && mobileSlot.imei)) === c.imei }"
            @click="onPickVideo(c.imei)"
          >
            <span :class="['dot', c.online ? 'on' : 'off']"></span>
            <span class="name">{{ c.name }}</span>
            <span class="tag">{{ c.serial }}</span>
          </div>
          <div v-if="!videoPickList.length" class="vl-sheet-empty">无匹配设备</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EZUIKit from "ezuikit-js";
import {
  getLatestLocations,
  getAllExamList,
  getDeviceListByExam,
  getAllDeviceList,
  getDeviceTree,
  getLocationListGroupByDevice2,
  getVideoToken,
  getVideoTokenFromRemoteServer,
} from "@/http/api/common";

const POLL_SECONDS = 2; // 轮询频率(秒);两台设备同车同行时,拉取越密漂移越小
const GLIDE_MS_MIN = 2000;  // 滑行最短时长
const GLIDE_MS_MAX = 12000; // 滑行最长时长:两点间按真实定位间隔匀速走,不压缩成冲刺
const STALE_MS = 60000;     // 定位点超过该时长不再外推(数据失效,冻结在最后位置)
const COAST_MAX_S = 10;     // 航位推算最长秒数(防止数据迟到时冲出路面)
const MAX_LIVE_STREAMS = 3;                 // 摄像头同时播放上限(PC 3路/手机1路),防网络卡顿与萤石并发超限

// 城市尺度下的距离(米)与位移航向(正北=0顺时针)
function distMeters(a, b) {
  const dx = (b[0] - a[0]) * Math.cos(((a[1] + b[1]) / 2) * Math.PI / 180) * 111320;
  const dy = (b[1] - a[1]) * 110540;
  return Math.sqrt(dx * dx + dy * dy);
}
function bearingDeg(a, b) {
  const dx = (b[0] - a[0]) * Math.cos((a[1] * Math.PI) / 180);
  const dy = b[1] - a[1];
  return (Math.atan2(dx, dy) * 180) / Math.PI;
}
// 航向角走最短弧:不归一化到0-360,让CSS transition沿短方向旋转(否则350°→10°会倒转340°)
function shortestArc(prev, target) {
  if (prev == null) return target;
  let d = (target - prev) % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return prev + d;
}

// 模块加载期即注入 viewport（早于 AMap 初始化），保证地图按真实视口尺寸创建
(function () {
  let m = document.querySelector('meta[name="viewport"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "viewport");
    document.head.appendChild(m);
  }
  m.setAttribute(
    "content",
    "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover"
  );
})();

export default {
  name: "VehicleLive",
  data() {
    return {
      cyMode: false, // ?p=cy 或 sessionStorage.cy_mode=1 时为朝阳模式（地图中心/缩放跟随）
      MAP: null,
      markers: {}, // imei -> AMap.Marker
      infoWindow: null,
      trailLine: null,
      vehicles: [],
      examOptions: [],
      examId: null,
      examImeiList: null, // 当前考试绑定的IMEI；null=全部车辆
      // ---- 设备分组(北京市设备多,按区/账号分组展示) ----
      deptOf: {},        // imei → 部门名(简短,如"05.丰台区")
      deptOrder: [],     // 部门显示顺序(后端按部门名排序)
      deptFilter: "",    // ""=全部分组
      groupOpen: {},     // 部门名 → 用户手动展开/收起状态(缺省=组内有在线车则展开)
      followImei: null,
      showTrail: true,
      loading: true,
      fetching: false,
      lastUpdate: "",
      pollTimer: null,
      fitted: false,
      nightMode: false,
      isMobile: false,
      panelOpen: false,
      // ---- 摄像头实时视频(萤石 EZUIKit,复用轨迹页方案) ----
      videoToken: "",
      serialMap: {},   // imei → 萤石设备序列号
      camBattery: {},  // imei → 萤石摄像头电量%(null=不支持/未上报；undefined=还没查过)
      liveProbe: {},   // imei → {liveReady, liveErrorCode, liveErrorMsg}
      players: {},     // imei → 播放器实例(不走响应式)
      pcVideoOn: true, // PC 端摄像头开关(默认开,保持原行为;关闭即销毁播放器释放萤石并发)
      pcPicked: [],    // PC 每路手动挑选的车辆 imei(≤3路,按路位顺序;空=按车辆顺序取前3)
      fsImei: null,    // 正在全屏观看的车辆 imei(PC)
      mobileVideoOn: false,
      mobileLiveImei: null,
      serialMapLoaded: false, // initVideo 是否成功建过映射(失败随轮询重试)
      sheetType: "",        // 移动端底部弹层: '' | 'exam' | 'dept'
      sheetKey: "",         // 弹层内选项过滤关键字
      videoPickerOpen: false, // 移动端摄像头选车弹层
      videoPickerKey: "",   // 选车弹层内搜索
      isWeChat: /MicroMessenger/i.test(navigator.userAgent),
    };
  },
  computed: {
    pageTitle() {
      return this.cyMode ? "朝阳区人事考试·试卷车实时定位" : "北京市人事考试·试卷车实时定位";
    },
    followedVehicle() {
      const imei = this.followImei;
      if (!imei) return null;
      return this.vehicles.find((v) => v.deviceIMEI === imei) || null;
    },
    // 关联了摄像头的车辆：跟随考试/区筛选(filteredVehicles)；在线车优先(离线摄像头开出画面也没意义)
    camCandidates() {
      return this.filteredVehicles
        .filter((v) => this.serialMap[v.deviceIMEI])
        .map((v) => ({
          imei: v.deviceIMEI,
          name: v.deviceName || v.deviceIMEI,
          serial: this.serialMap[v.deviceIMEI],
          online: !!v.online,
        }))
        .sort((a, b) => (a.online === b.online ? 0 : a.online ? -1 : 1));
    },
    onlineCount() {
      return this.vehicles.filter((v) => v.online).length;
    },
    // 按区筛选后的车辆(考试筛选在服务端 imeiList 完成,区筛选纯前端)
    filteredVehicles() {
      if (!this.deptFilter) return this.vehicles;
      return this.vehicles.filter((v) => (this.deptOf[v.deviceIMEI] || "未分组") === this.deptFilter);
    },
    // 移动端弹层内按关键字过滤的选项
    sheetExamOptions() {
      const kw = this.sheetKey.trim().toLowerCase();
      if (!kw) return this.examOptions;
      return this.examOptions.filter((e) => (e.examName || "").toLowerCase().indexOf(kw) >= 0);
    },
    sheetDeptOptions() {
      const kw = this.sheetKey.trim().toLowerCase();
      if (!kw) return this.deptOrder;
      return this.deptOrder.filter((d) => String(d).toLowerCase().indexOf(kw) >= 0);
    },
    // 移动端摄像头选车弹层列表:跟随当前筛选,支持弹层内关键字
    videoPickList() {
      const kw = this.videoPickerKey.trim().toLowerCase();
      return this.camCandidates.filter(
        (c) => !kw || (c.name + " " + c.imei + " " + c.serial).toLowerCase().indexOf(kw) >= 0
      );
    },
    currentExamName() {
      const e = this.examOptions.find((x) => x.id === this.examId);
      return e ? e.examName : "";
    },
    // 按部门(区/账号)分组,部门顺序沿用后端返回;快照里部门表没有的设备归"未分组"兜底
    vehicleGroups() {
      const map = {};
      const order = [];
      this.deptOrder.forEach((n) => {
        map[n] = { name: n, list: [] };
        order.push(n);
      });
      if (!map["未分组"]) {
        map["未分组"] = { name: "未分组", list: [] };
        order.push("未分组");
      }
      this.filteredVehicles.forEach((v) => {
        const n = this.deptOf[v.deviceIMEI] || "未分组";
        if (!map[n]) {
          map[n] = { name: n, list: [] };
          order.push(n);
        }
        map[n].list.push(v);
      });
      return order
        .map((n) => {
          const g = map[n];
          g.onlineCount = g.list.filter((v) => v.online).length;
          return g;
        })
        .filter((g) => g.list.length > 0);
    },
    hasAnyCamera() {
      return this.camCandidates.length > 0;
    },
    // 顶栏📹按钮的开关状态:PC/手机各记忆一份(PC 默认开,手机默认关)
    videoOn() {
      return this.isMobile ? this.mobileVideoOn : this.pcVideoOn;
    },
    // 摄像头车辆超过同看上限时,每一路标题变成下拉可换车
    canPickCams() {
      return this.camCandidates.length > MAX_LIVE_STREAMS;
    },
    // PC 右侧竖排,最多 3 路:手动挑选的优先(按路位顺序),不够的按车辆顺序补齐
    liveSlots() {
      const cands = this.camCandidates;
      const picked = this.pcPicked.filter((imei) => cands.some((c) => c.imei === imei));
      const fill = cands.filter((c) => picked.indexOf(c.imei) === -1).map((c) => c.imei);
      return picked
        .concat(fill)
        .slice(0, MAX_LIVE_STREAMS)
        .map((imei) => this.decorateSlot(cands.find((c) => c.imei === imei)));
    },
    // 全屏观看的那一路(沿用探测状态;车辆被移除时自动退出全屏,见 watcher)
    fsSlot() {
      if (!this.fsImei) return null;
      const c = this.camCandidates.find((x) => x.imei === this.fsImei);
      return c ? this.decorateSlot(c) : null;
    },
    // 移动端单路:手动切换 > 跟随车辆 > 第一台
    mobileSlot() {
      const cands = this.camCandidates;
      if (!cands.length) return null;
      const want = this.mobileLiveImei || this.followImei;
      const pick = cands.find((c) => c.imei === want) || cands[0];
      return this.decorateSlot(pick);
    },
  },
  watch: {
    // 区筛选变化：地图点位跟随重绘(camCandidates 由 watcher 接力,右侧视频自动同步)
    deptFilter() {
      this.renderMarkers(this.filteredVehicles.filter((v) => v.online));
    },
    // 车辆/序列号就绪或每次列表刷新 → 补探测 + 同步播放器 + 补拉摄像头电量
    camCandidates() {
      // 正在全屏的车已不可选(被移除/换绑)时先退出全屏
      if (this.fsImei && !this.camCandidates.some((c) => c.imei === this.fsImei)) {
        this.closeFullscreen();
      }
      this.refreshVideo();
      this.fetchCamBatteries();
    },
    followImei(imei) {
      if (this.isMobile && imei && this.serialMap[imei] && this.mobileLiveImei !== imei) {
        this.mobileLiveImei = imei;
        this.refreshVideo();
      }
    },
    mobileVideoOn(on) {
      if (on) this.refreshVideo();
      else this.syncPlayers();
    },
    pcVideoOn(on) {
      if (on) this.refreshVideo();
      else {
        if (this.fsImei) this.closeFullscreen();
        this.syncPlayers();
      }
    },
    // 布局形态切换(旋转/缩放跨过768)时容器不同,播放器全部重建
    isMobile() {
      if (this.fsImei) this.closeFullscreen();
      this.destroyAllPlayers();
      this.refreshVideo();
    },
  },
  created() {
    // cy 模式判定与 LocationTrace 同口径：URL ?p=cy 或 sessionStorage 标记
    this.cyMode = this.$route.query.p === 'cy' || sessionStorage.getItem('cy_mode') === '1';
    if (this.$route.query.p === 'cy') sessionStorage.setItem('cy_mode', '1');
    // 地图默认白天模式；夜间选择记忆在本机
    this.nightMode = localStorage.getItem("vehicleLiveNight") === "1";
    this.isMobile = window.innerWidth <= 768;
    // 车标运动状态(imei→插值/外推参数),高频更新不走Vue响应式
    this.markerMeta = {};
    // 各车近期轨迹缓存(用于贴路滑行),imei→{pts:[{t,pos}]}
    this.trailCache = {};
    this.pollCount = 0;
    this.videoInitTries = 0; // initVideo 失败重试计数(非响应式)
    this.players = {};
    this.fsPlayer = null; // 全屏播放器实例(不走响应式)
    this._playerSig = "";
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("keydown", this.onKeydown);
    this.mapInit();
    this.startMotionLoop();
    this.loadExams();
    this.loadDeviceTree();
    this.fetchLatest();
    this.pollTimer = setInterval(() => this.fetchLatest(), POLL_SECONDS * 1000);
    this.initVideo();
  },
  beforeDestroy() {
    clearInterval(this.pollTimer);
    this.destroyAllPlayers();
    this.destroyFsPlayer();
    if (this.motionRaf) cancelAnimationFrame(this.motionRaf);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("keydown", this.onKeydown);
    if (this.MAP) this.MAP.destroy();
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth <= 768;
    },
    toggleMode() {
      this.nightMode = !this.nightMode;
      localStorage.setItem("vehicleLiveNight", this.nightMode ? "1" : "0");
      if (this.MAP) {
        this.MAP.setMapStyle(this.nightMode ? "amap://styles/darkblue" : "amap://styles/normal");
      }
    },
    onPanelHead() {
      if (this.isMobile) this.panelOpen = !this.panelOpen;
    },
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push(process.env.VUE_APP_DEFAULT_ROUTE || "/home_cy");
    },
    mapInit() {
      // 与 LocationTrace 同口径：cy 模式定位朝阳区，否则北京市
      const mapCenter = this.cyMode ? [116.540051, 39.941443] : [116.406265, 39.903324];
      const mapZoom = this.cyMode ? 12 : 10;
      this.MAP = new AMap.Map("vehicleMap", {
        mapStyle: this.nightMode ? "amap://styles/darkblue" : "amap://styles/normal",
        center: mapCenter,
        zoom: mapZoom,
        resizeEnable: true,
      });
      this.infoWindow = new AMap.InfoWindow({ isCustom: true, offset: new AMap.Pixel(0, -46) });
    },
    // ---- 移动端弹层交互 ----
    openSheet(type) {
      this.sheetKey = "";
      this.sheetType = type;
    },
    onPickExam(examId) {
      this.sheetType = "";
      if (examId === this.examId) return;
      this.examId = examId;
      this.onExamChange(examId);
    },
    onPickDept(d) {
      this.sheetType = "";
      this.deptFilter = d;
    },
    onPickVideo(imei) {
      this.videoPickerOpen = false;
      this.mobileLiveImei = imei;
      this.refreshVideo();
    },
    loadExams() {
      getAllExamList().then((res) => {
        this.examOptions = (res.data && res.data.length ? res.data : []).slice().reverse();
      });
    },
    // 拉部门(区/账号)分组的全量设备树,建 imei→部门 映射(一次)
    loadDeviceTree() {
      getDeviceTree().then((res) => {
        const groups = res.data || [];
        const deptOf = {};
        const order = [];
        groups.forEach((g) => {
          const name = String(g.deptName || "").split("(")[0] || "未分组";
          if (order.indexOf(name) < 0) order.push(name);
          (g.deviceList || []).forEach((d) => {
            if (d.deviceIMEI) deptOf[d.deviceIMEI] = name;
          });
        });
        this.deptOf = deptOf;
        this.deptOrder = order;
      });
    },
    // 组展开缺省规则:有在线车展开;用户点过以用户为准
    isGroupOpen(g) {
      const st = this.groupOpen[g.name];
      return st === undefined ? g.onlineCount > 0 : st;
    },
    toggleGroup(g) {
      this.$set(this.groupOpen, g.name, !this.isGroupOpen(g));
    },
    expandAllGroups() {
      const st = {};
      this.vehicleGroups.forEach((g) => (st[g.name] = true));
      this.groupOpen = st;
    },
    collapseAllGroups() {
      const st = {};
      this.vehicleGroups.forEach((g) => (st[g.name] = false));
      this.groupOpen = st;
    },
    onExamChange(examId) {
      this.followImei = null;
      this.closeInfo();
      if (!examId) {
        this.examImeiList = null;
        this.fetchLatest();
        return;
      }
      getDeviceListByExam({ examIdList: [examId] }).then((res) => {
        const imeis = [];
        (res.data || []).forEach((dept) => {
          (dept.deviceList || []).forEach((d) => {
            if (d.deviceIMEI) imeis.push(d.deviceIMEI);
          });
        });
        this.examImeiList = imeis;
        this.fetchLatest();
      });
    },
    fetchLatest() {
      if (this.fetching) return;
      this.fetching = true;
      getLatestLocations(this.examImeiList)
        .then((res) => {
          const list = res.data || [];
          this.vehicles = list;
          this.lastUpdate = new Date().toLocaleTimeString("zh-CN", { hour12: false });
          // 地图只画筛选后在线车标：离线设备停在最后坐标(多为考点),全部画出会让地图糊成一片
          this.renderMarkers(this.filteredVehicles.filter((v) => v.online));
          // 摄像头映射初始化失败的兜底重试(如页面加载时后端正在重启)
          if (!this.serialMapLoaded && this.videoInitTries < 5) {
            this.videoInitTries += 1;
            this.initVideo();
          }
          if (this.followImei) this.refreshTrail();
          // 每3个轮询周期拉一次可见车辆近期轨迹(贴路滑行的路径来源);只拉筛选后的在线车
          this.pollCount += 1;
          if (this.pollCount % 3 === 1) {
            this.refreshTrails(this.filteredVehicles.filter((v) => v.online).map((x) => x.deviceIMEI));
          }
        })
        .finally(() => {
          this.loading = false;
          this.fetching = false;
        });
    },
    markerContent(v, angle) {
      const rot = angle == null ? Number(v.direction) || 0 : angle;
      const online = v.online;
      const imgStyle = online ? `transform:rotate(${rot}deg);` : "filter:grayscale(1);opacity:.55;";
      const label = v.deviceName || v.deviceIMEI;
      return (
        '<div class="vl-marker' + (online ? "" : " offline") + '">' +
        '<img src="/imgs/car.png" style="' + imgStyle + '" />' +
        '<span class="vl-marker-label">' + label + "</span></div>"
      );
    },
    // 收到定位帧:新点→重设滑行区间/位移航向;无新点→静止车贴回真实点(行驶车由动画循环外推)
    applyFix(imei, marker, v, pos) {
      const now = Date.now();
      const meta = this.markerMeta[imei] || (this.markerMeta[imei] = {
        fromPos: pos.slice(), toPos: pos.slice(), fixPos: pos.slice(),
        fromTime: now, toTime: now, fixTime: 0, speed: 0, angle: null, vLng: 0, vLat: 0,
      });
      const fixTime = this.parseTime(v.locationTime);
      if (fixTime && fixTime > (meta.fixTime || 0)) {
        const prevFixTime = meta.fixTime || 0; // 先取上一帧时间(后面会被覆写)
        // 车头朝向 = 上一真实点→本真实点的位移方向(与视觉移动一致);
        // 位移过小(<15m,如等灯/堵车)沿用原朝向或设备罗盘航向
        let bearing = meta.angle;
        if (meta.fixPos && distMeters(meta.fixPos, pos) > 15) bearing = bearingDeg(meta.fixPos, pos);
        else if (Number(v.direction) > 0) bearing = Number(v.direction);
        const cur = marker.getPosition();
        meta.fromPos = cur ? [cur.getLng(), cur.getLat()] : pos.slice();
        meta.toPos = pos.slice();
        meta.fixPos = pos.slice();
        // 滑行时长 = 上一帧→本帧的真实定位间隔(裁剪到[2s,12s]),按真实节奏匀速移动;
        // 首帧无参考间隔,用最短滑行
        meta.interval =
          prevFixTime > 0 ? Math.min(Math.max(fixTime - prevFixTime, GLIDE_MS_MIN), GLIDE_MS_MAX) : GLIDE_MS_MIN;
        meta.fromTime = now;
        meta.toTime = now + meta.interval;
        meta.fixTime = fixTime;
        meta.speed = Number(v.speed) || 0;
        // 沿轨迹贴路滑行:路径 = 当前位置 → 两真实点之间的历史轨迹点 → 新真实点;
        // 两点之间没有轨迹点时退化为直线(与公交"沿已知线路插值"同思路,我们的线路=车走过的轨迹)
        const sub = this.subPathPoints(imei, prevFixTime, fixTime);
        meta.pathPts = [meta.fromPos].concat(sub, [meta.toPos]);
        this.recalcPath(meta);
        meta.segIdx = 1;
        meta.lastV = v;
        if (bearing != null && !Number.isNaN(bearing)) meta.angle = shortestArc(meta.angle, bearing);
        marker.setContent(this.markerContent(v, meta.angle));
      } else if ((meta.speed || 0) <= 1) {
        marker.setPosition(meta.toPos);
      }
    },
    // 取两个定位时刻之间的历史轨迹点(贴路滑行的中间路径)
    subPathPoints(imei, fromT, toT) {
      const pts = (this.trailCache[imei] || {}).pts || [];
      const sub = [];
      for (const p of pts) {
        if (p.t > fromT && p.t < toT) sub.push(p.pos);
      }
      return sub;
    },
    // 计算路径累计弧长(供按里程匀速插值)与整体外推速度
    recalcPath(meta) {
      meta.cum = [0];
      let total = 0;
      for (let i = 1; i < meta.pathPts.length; i++) {
        total += distMeters(meta.pathPts[i - 1], meta.pathPts[i]);
        meta.cum.push(total);
      }
      meta.pathLen = total;
      // 外推速度按"真实定位间隔"折算(绝不是滑行压缩比!),
      // 否则推算速度被放大数倍,数据一迟到车标就冲出真实位置再弹回,表现为漂移
      const seg = (meta.interval || GLIDE_MS_MIN) / 1000;
      const first = meta.pathPts[0];
      const last = meta.pathPts[meta.pathPts.length - 1];
      meta.vLng = (last[0] - first[0]) / seg;
      meta.vLat = (last[1] - first[1]) / seg;
    },
    // 拉取可见车辆最近15分钟轨迹(贴路滑行的路径来源),每3个轮询周期一次
    refreshTrails(imeiList) {
      if (!imeiList || !imeiList.length) return;
      const end = new Date();
      const start = new Date(end.getTime() - 15 * 60000);
      // 后端 Date 字段只认 yyyyMMddHHmmss 紧凑格式,带空格的 yyyy-MM-dd HH:mm:ss 会报字段类型错误
      const fmt = (d) =>
        d.getFullYear() + String(d.getMonth() + 1).padStart(2, "0") + String(d.getDate()).padStart(2, "0") +
        String(d.getHours()).padStart(2, "0") + String(d.getMinutes()).padStart(2, "0") + String(d.getSeconds()).padStart(2, "0");
      getLocationListGroupByDevice2({
        imeiList: imeiList,
        locationStartDate: fmt(start),
        locationEndDate: fmt(end),
      })
        .then((res) => {
          (res.data || []).forEach((g) => {
            const pts = (g.dataList || [])
              .map((p) => ({ t: this.parseTime(p.locationTime), pos: [Number(p.sparkLongtitude), Number(p.sparkLatitude)] }))
              .filter((p) => p.t && p.pos[0] && p.pos[1])
              .sort((a, b) => a.t - b.t);
            this.trailCache[g.deviceIMEI] = { pts: pts };
          });
        })
        .catch(() => {});
    },
    // 运动循环(rAF):优先沿轨迹路径按弧长匀速滑行(贴路);到点后若数据未更新且
    // 车速>1,按末段速度航向继续航位推算(业内实时车辆展示的标准做法)
    startMotionLoop() {
      const step = () => {
        this.motionRaf = requestAnimationFrame(step);
        if (!this.MAP) return;
        const now = Date.now();
        for (const imei in this.markerMeta) {
          const marker = this.markers[imei];
          const m = this.markerMeta[imei];
          if (!marker || !m) continue;
          const usePath = m.pathPts && m.pathPts.length > 1 && (m.pathLen || 0) > 1;
          let lng, lat;
          if (usePath && now < m.toTime) {
            // 沿路径按累计弧长匀速前进
            const t = Math.min(Math.max((now - m.fromTime) / (m.toTime - m.fromTime), 0), 1);
            const d = t * m.pathLen;
            let i = 1;
            while (i < m.cum.length - 1 && m.cum[i] < d) i++;
            const a = m.pathPts[i - 1];
            const b = m.pathPts[i];
            const segLen = m.cum[i] - m.cum[i - 1] || 1e-9;
            const f = (d - m.cum[i - 1]) / segLen;
            lng = a[0] + (b[0] - a[0]) * f;
            lat = a[1] + (b[1] - a[1]) * f;
            // 进入新路段:车头转向该路段方向(路段足够长才转,过滤GPS毛刺)
            if (i !== m.segIdx) {
              m.segIdx = i;
              if (segLen > 12 && m.angle != null) {
                m.angle = shortestArc(m.angle, bearingDeg(a, b));
                marker.setContent(this.markerContent(m.lastV || {}, m.angle));
              }
            }
          } else if (now < m.toTime) {
            const t = (now - m.fromTime) / (m.toTime - m.fromTime);
            lng = m.fromPos[0] + (m.toPos[0] - m.fromPos[0]) * t;
            lat = m.fromPos[1] + (m.toPos[1] - m.fromPos[1]) * t;
          } else if (m.speed > 1 && now - m.fixTime < STALE_MS) {
            const over = Math.min((now - m.toTime) / 1000, COAST_MAX_S);
            lng = m.toPos[0] + m.vLng * over;
            lat = m.toPos[1] + m.vLat * over;
          } else {
            lng = m.toPos[0];
            lat = m.toPos[1];
          }
          marker.setPosition([lng, lat]);
          // 跟车时信息窗锚着车走
          if (imei === this.followImei && this.infoWindow) this.infoWindow.setPosition([lng, lat]);
        }
      };
      this.motionRaf = requestAnimationFrame(step);
    },
    parseTime(s) {
      if (!s) return 0;
      const t = new Date(String(s).replace(/-/g, "/")).getTime();
      return Number.isNaN(t) ? 0 : t;
    },
    renderMarkers(list) {
      if (!this.MAP) return;
      const seen = {};
      list.forEach((v) => {
        const imei = v.deviceIMEI;
        const pos = [Number(v.longitude), Number(v.latitude)];
        if (!pos[0] || !pos[1]) return;
        seen[imei] = true;
        let marker = this.markers[imei];
        if (!marker) {
          marker = new AMap.Marker({
            position: pos,
            content: this.markerContent(v, null),
            anchor: "bottom-center",
            zIndex: 120,
          });
          marker.on("click", () => this.followVehicle(v));
          marker.setMap(this.MAP);
          this.markers[imei] = marker;
        }
        this.applyFix(imei, marker, v, pos);
        // 跟车时地图跟随车辆真实点（setCenter 立即模式，v1 起验证过的安全用法）
        if (v.deviceIMEI === this.followImei && pos[0] && pos[1]) this.MAP.setCenter(pos, true);
      });
      // 移除已不在列表里的 marker（如切换考试过滤）
      Object.keys(this.markers).forEach((imei) => {
        if (!seen[imei]) {
          this.MAP.remove(this.markers[imei]);
          delete this.markers[imei];
          delete this.markerMeta[imei];
        }
      });
      if (!this.fitted && list.length) {
        this.fitted = true;
        this.fitAll();
      }
    },
    fitAll() {
      const overlays = Object.values(this.markers);
      // 桌面左侧被 380px 面板、右侧被 300px 视频列遮挡;手机上屏幕窄,均匀避让即可
      const avoid = this.isMobile ? [60, 60, 60, 60] : [80, 360, 80, 420];
      if (overlays.length) this.MAP.setFitView(overlays, true, avoid, 14);
    },
    followVehicle(v) {
      this.followImei = v.deviceIMEI;
      const pos = [Number(v.longitude), Number(v.latitude)];
      if (pos[0] && pos[1]) this.MAP.setZoomAndCenter(15, pos);
      this.openInfo(v);
      this.refreshTrail();
      if (this.isMobile) {
        this.panelOpen = false;
        // 摄像头开关是独立功能:选车只切换内容(见 followImei watcher),不改变开/关状态
      }
    },
    openInfo(v) {
      const camSerial = this.serialMap[v.deviceIMEI] || "";
      const camBatt = this.camBattery[v.deviceIMEI];
      const rows = [
        ["定位时间", v.locationTime || "--"],
        ["GPS电量", v.electric != null ? Math.round(v.electric) + "%" : "--"],
        ["状态", v.online ? "在线" : "离线（心跳超时）"],
        ["设备IMEI", v.deviceIMEI],
        ["萤石摄像头ID", camSerial || "未绑定"],
        ["摄像头电量", camBatt != null ? camBatt + "%" : "--"],
      ];
      const html =
        '<div class="vl-infowin">' +
        '<div class="vl-infowin-title">' + (v.deviceName || v.deviceIMEI) + "</div>" +
        rows
          .map((r) => '<div class="vl-infowin-row"><span>' + r[0] + '</span><b>' + r[1] + "</b></div>")
          .join("") +
        "</div>";
      this.infoWindow.setContent(html);
      this.infoWindow.open(this.MAP, [Number(v.longitude), Number(v.latitude)]);
    },
    closeInfo() {
      if (this.infoWindow) this.infoWindow.close();
      this.clearTrail();
    },
    clearTrail() {
      if (this.trailLine) {
        this.MAP.remove(this.trailLine);
        this.trailLine = null;
      }
    },
    refreshTrail() {
      if (!this.followImei || !this.showTrail) {
        this.clearTrail();
        return;
      }
      const d = new Date();
      const day =
        d.getFullYear() + String(d.getMonth() + 1).padStart(2, "0") + String(d.getDate()).padStart(2, "0");
      getLocationListGroupByDevice2({
        imeiList: [this.followImei],
        locationStartDate: day + "000000",
        locationEndDate: day + "235959",
      }).then((res) => {
        this.clearTrail();
        const group = (res.data || []).find((g) => g.deviceIMEI === this.followImei);
        const points = (group && group.dataList) || [];
        const path = points
          .map((p) => [Number(p.sparkLongtitude), Number(p.sparkLatitude)])
          .filter((p) => p[0] && p[1]);
        if (path.length > 1) {
          this.trailLine = new AMap.Polyline({
            path: path,
            strokeColor: "#00d3ff",
            strokeWeight: 4,
            strokeOpacity: 0.9,
            showDir: true,
            zIndex: 60,
          });
          this.MAP.add(this.trailLine);
        }
      });
    },
    // ================= 摄像头实时视频(萤石,复用轨迹页方案) =================
    toggleVideo() {
      if (this.isMobile) this.mobileVideoOn = !this.mobileVideoOn;
      else this.pcVideoOn = !this.pcVideoOn;
    },
    async fetchVideoToken() {
      try {
        const res = await getVideoToken();
        if (res.code !== 200 || !res.data) {
          console.error("获取视频Token失败:", res && res.msg);
          return;
        }
        let token = res.data;
        try {
          // 过期校验:10002 = accessToken 无效,从远端兜底接口重取
          const check = await this.fetchEzvizDevicePage(token, 0);
          if (check && String(check.code) === "10002") {
            const refresh = await getVideoTokenFromRemoteServer();
            if (refresh.code === 200 && (refresh.data || refresh.msg)) {
              token = refresh.data || refresh.msg;
            } else {
              console.error("视频Token过期且刷新失败");
              return;
            }
          }
        } catch (e) {
          // 校验请求失败不阻塞,先用当前 token 试一把
        }
        this.videoToken = token;
        // camCandidates 就绪可能早于token(watcher触发时token未到),token就绪后补拉电量
        if (this.camCandidates.length) this.fetchCamBatteries();
      } catch (e) {
        console.error("获取视频Token异常:", e);
      }
    },
    ensureVideoToken() {
      if (this.videoToken) return Promise.resolve(this.videoToken);
      if (this._tokenPromise) return this._tokenPromise;
      this._tokenPromise = this.fetchVideoToken().then(() => {
        this._tokenPromise = null;
        return this.videoToken;
      });
      return this._tokenPromise;
    },
    // 萤石开放平台 lapp 接口(直连,form 表单)
    async postEzvizApi(path, form) {
      const body = Object.keys(form)
        .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(form[k] == null ? "" : form[k]))
        .join("&");
      const response = await fetch("https://open.ys7.com" + path, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body,
      });
      return response.json();
    },
    async fetchEzvizDevicePage(token, pageStart) {
      const url =
        "https://open.ys7.com/api/lapp/device/list" +
        "?accessToken=" +
        encodeURIComponent(token) +
        "&pageStart=" +
        pageStart +
        "&pageSize=50";
      const response = await fetch(url, { method: "POST" });
      return response.json();
    },
    /**
     * 逐台探测真实可播状态。
     * 萤石 device/list 只返回已开通云直播的设备,不能作为"能不能看"的判据,
     * 必须 device/info(在线) + live/address/get(取流) 两步确认。
     */
    async probeEzvizDevice(serial) {
      const result = { liveReady: false, liveErrorCode: "", liveErrorMsg: "" };
      const token = this.videoToken || (await this.ensureVideoToken());
      if (!token) {
        result.liveErrorMsg = "视频Token获取失败";
        return result;
      }
      let info;
      try {
        info = await this.postEzvizApi("/api/lapp/device/info", { accessToken: token, deviceSerial: serial });
      } catch (e) {
        result.liveErrorMsg = "查询设备信息失败";
        return result;
      }
      if (!info || String(info.code) !== "200") {
        result.liveErrorMsg = (info && info.msg) || "设备不存在或无权限";
        return result;
      }
      if (Number((info.data || {}).status) !== 1) {
        result.liveErrorCode = "20007";
        result.liveErrorMsg = "设备当前离线";
        return result;
      }
      let addr;
      try {
        addr = await this.postEzvizApi("/api/lapp/v2/live/address/get", {
          accessToken: token,
          deviceSerial: serial,
          channelNo: "1",
          protocol: "2",
          quality: "2",
          type: "1",
        });
      } catch (e) {
        result.liveErrorMsg = "取流探测失败";
        return result;
      }
      if (addr && String(addr.code) === "200" && addr.data && addr.data.url) {
        result.liveReady = true;
        return result;
      }
      result.liveErrorCode = addr ? String(addr.code) : "";
      result.liveErrorMsg = (addr && addr.msg) || "无法获取播放地址";
      return result;
    },
    // 把萤石错误码翻译成能直接照做的提示
    ezvizErrorHint(code) {
      switch (String(code)) {
        case "60060": return "未开通直播服务，请在萤石开放平台开通云直播";
        case "60019": return "设备开启了视频加密，请在萤石APP关闭";
        case "20007": return "设备当前离线，请检查设备供电与4G网络";
        case "20002": return "设备不存在或序列号有误";
        case "20018": return "萤石云账号不拥有此设备";
        case "9048":
        case "5546":
        case "5547": return "账号并发路数已达上限，需升级套餐";
        default: return "";
      }
    },
    decorateSlot(c) {
      const p = this.liveProbe[c.imei];
      return {
        ...c,
        ready: !!(p && p.liveReady),
        err: p ? p.liveErrorMsg || "暂无视频" : "连接中…",
        hint: p ? this.ezvizErrorHint(p.liveErrorCode) : "",
      };
    },
    // 拉全量设备清单,建 imei→萤石序列号 映射(一次);完成后 camCandidates 有值,由 watcher 接力
    // 映射不依赖萤石token(卡片/弹窗的摄像头ID展示无需token);token 失败只影响播放,由打开视频时按需重试。
    // 页面加载时若后端恰在重启等瞬时故障,靠 fetchLatest 轮询重试兜底(videoInitTries 限次)
    async initVideo() {
      try {
        const res = await getAllDeviceList({ currentPage: 1, pageSize: 1000 });
        const map = {};
        ((res.data && res.data.list) || []).forEach((d) => {
          if (d.deviceIMEI && d.videoDeviceSerial) map[d.deviceIMEI] = d.videoDeviceSerial;
        });
        this.serialMap = map;
        this.serialMapLoaded = true;
      } catch (e) {
        console.warn("摄像头映射初始化失败(将随轮询重试):", e);
      }
      try {
        await this.ensureVideoToken();
      } catch (e) {
        console.warn("视频Token初始化失败(打开视频时重试):", e);
      }
    },
    // 查每台摄像头的电池电量(萤石 OTAP 属性 PowerMgr/SurplusPower，返回0-100)。
    // 只查筛选范围内且在线的车辆摄像头：账号下绑定上百台,逐台全查会瞬间发出
    // 上百个请求刷爆网络面板;离线车上线后 camCandidates 变化会自动补查。
    // 每台只查一次并缓存：电量变化慢，且轮询触发频繁不宜反复请求
    async fetchCamBatteries() {
      if (!this.videoToken) return;
      const need = this.camCandidates.filter((c) => c.online && this.camBattery[c.imei] === undefined);
      for (const c of need) {
        let batt = null;
        try {
          const url =
            "https://open.ys7.com/api/v3/otap/prop/" + c.serial +
            "/global/0/PowerMgr/SurplusPower?accessToken=" + encodeURIComponent(this.videoToken);
          const resp = await fetch(url, { method: "GET" });
          const json = await resp.json();
          if (json && json.meta && Number(json.meta.code) === 200 && json.data != null) {
            const n = Number(json.data);
            if (!Number.isNaN(n) && n >= 0) batt = Math.round(n);
          }
        } catch (e) {
          // 查询失败记为 null，不重试
        }
        this.$set(this.camBattery, c.imei, batt);
      }
    },
    // 探测当前应展示的设备(结果缓存,只探一次),然后同步播放器
    async refreshVideo() {
      if (!this.videoToken) {
        // 初始化时token可能恰逢失败(后端重启等),打开视频时再补一次
        const t = await this.ensureVideoToken().catch(() => null);
        if (!t) return;
      }
      if (!this.camCandidates.length) return;
      // 先挂播放器再并行探测：播放器自己会取流拉流，若等探测完成才挂载，
      // 切换摄像头会多出"探测串行等待+播放器二次取流"共1-2秒的黑屏
      this.syncPlayers();
      const imeis = this.isMobile
        ? this.mobileSlot
          ? [this.mobileSlot.imei]
          : []
        : this.liveSlots.map((s) => s.imei);
      const need = imeis.filter((i) => !this.liveProbe[i]);
      for (const imei of need) {
        const st = await this.probeEzvizDevice(this.serialMap[imei]);
        this.$set(this.liveProbe, imei, st);
        // 探测完成修正在线/错误状态(浮层消失或显示错误提示)
        this.syncPlayers();
      }
    },
    // 按"当前应显示集合"增删播放器;切走即销毁,防 WebRTC 会话泄漏占满萤石并发。
    // 挂载不等探测(ready)结果:未ready时浮层显示"连接中…",视频出帧即透出,探测并行修正状态。
    // 全屏中的那一路不进小窗集合(由 open/closeFullscreen 单独管理)
    syncPlayers() {
      const slots = this.isMobile
        ? this.mobileVideoOn && this.mobileSlot
          ? [this.mobileSlot]
          : []
        : this.pcVideoOn
          ? this.liveSlots
          : [];
      const targets = slots
        .map((s) => s.imei)
        .slice(0, MAX_LIVE_STREAMS)
        .filter((imei) => imei !== this.fsImei);
      const sig = targets.join(",");
      if (sig === this._playerSig) return;
      this._playerSig = sig;
      Object.keys(this.players).forEach((imei) => {
        if (targets.indexOf(imei) === -1) this.destroyPlayer(imei);
      });
      this.$nextTick(() => {
        targets.forEach((imei) => {
          if (this.players[imei]) return;
          this.mountPlayer(imei, "vlLive-" + imei);
        });
      });
    },
    // 在指定容器创建播放器;全屏容器单独挂在 fsPlayer,避免与 players 池混键
    mountPlayer(imei, domId) {
      if (!document.getElementById(domId)) return;
      try {
        const player = new EZUIKit.EZUIKitPlayer({
          id: domId,
          accessToken: this.videoToken,
          url: "ezopen://open.ys7.com/" + this.serialMap[imei] + "/1.hd.live",
          width: "100%",
          height: "100%",
          autoplay: true,
          audio: 0,
          template: "standard",
          plugin: ["fullScreen"],
        });
        if (domId === "vlLive-fs") this.fsPlayer = player;
        else this.players[imei] = player;
      } catch (e) {
        console.warn("播放器初始化失败:", imei, e);
      }
    },
    destroyPlayer(imei) {
      const p = this.players[imei];
      try {
        if (p) {
          if (p.stop) p.stop();
          if (p.destroy) p.destroy();
        }
      } catch (e) {
        // 忽略销毁异常
      }
      delete this.players[imei];
    },
    destroyAllPlayers() {
      Object.keys(this.players).forEach((imei) => this.destroyPlayer(imei));
      this._playerSig = "";
    },
    // 名称压成单行(接口设备名里带换行,下拉选项里会折行)
    flatName(s) {
      return String(s == null ? "" : s).replace(/\s+/g, " ").trim();
    },
    // 第 idx 路换车:整表覆盖挑选列表(liveSlots 已含补齐,直接取当前路位顺序即可)
    onPickSlot(idx, imei) {
      const imeis = this.liveSlots.map((s) => s.imei);
      if (imeis[idx] === imei) return;
      imeis[idx] = imei;
      this.pcPicked = imeis;
      this.refreshVideo();
    },
    // ================= 全屏观看(先销毁小窗播放器再在全屏容器重建,流路数不叠加) =================
    openFullscreen(imei) {
      if (this.isMobile || this.fsImei === imei) return;
      const slot = this.liveSlots.find((s) => s.imei === imei);
      if (!slot || !slot.ready) return;
      if (this.fsImei) this.destroyFsPlayer(); // 换车全屏:先释放上一路
      this.fsImei = imei;
      this.destroyPlayer(imei);
      this.syncPlayers(); // 签名归一为"不含全屏车",小窗不会重建
      this.$nextTick(() => this.mountPlayer(imei, "vlLive-fs"));
    },
    closeFullscreen() {
      if (!this.fsImei) return;
      this.fsImei = null;
      this.destroyFsPlayer();
      this.syncPlayers(); // 签名重新包含该车→小窗播放器自动重建
    },
    destroyFsPlayer() {
      const p = this.fsPlayer;
      try {
        if (p) {
          if (p.stop) p.stop();
          if (p.destroy) p.destroy();
        }
      } catch (e) {
        // 忽略销毁异常
      }
      this.fsPlayer = null;
    },
    onKeydown(e) {
      if (e.key === "Escape" && this.fsImei) this.closeFullscreen();
    },
    switchMobileCam(dir) {
      const cands = this.camCandidates;
      if (!cands.length) return;
      const cur = this.mobileSlot ? this.mobileSlot.imei : null;
      const idx = cands.findIndex((c) => c.imei === cur);
      this.mobileLiveImei = cands[(idx + dir + cands.length) % cands.length].imei;
      this.refreshVideo();
    },
  },
};
</script>

<style scoped lang="scss">
.vehicle-live {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #04122b;
  #vehicleMap {
    width: 100%;
    height: 100%;
  }
}

.vl-mode-btn,
.vl-cars-btn {
  position: fixed;
  right: 12px;
  z-index: 300;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(6, 27, 56, 0.72);
  border: 1px solid rgba(0, 211, 255, 0.28);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  cursor: pointer;
  user-select: none;
  &:active {
    transform: scale(0.92);
  }
}
.vl-mode-btn {
  bottom: 46px;
}
/* 试卷车列表按钮:仅移动端显示(叠在月亮按钮上方),桌面用左侧面板 */
.vl-cars-btn {
  display: none;
}

/* ---- 摄像头视频 ---- */
/* 顶栏📹开关:桌面/手机共用(桌面在单行顶栏内与返回按钮同序排其前;手机在标题行内,见下方媒体查询) */
.vl-cam-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  order: 4;
  width: 30px;
  height: 28px;
  margin-right: 8px;
  border: 1px solid rgba(0, 211, 255, 0.35);
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  &:active {
    transform: scale(0.92);
  }
}
.vl-videos {
  position: absolute;
  top: 64px;
  right: 12px;
  width: 300px;
  z-index: 150;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.vl-video-card,
.vl-video-single {
  background: rgba(6, 27, 56, 0.88);
  border: 1px solid rgba(0, 211, 255, 0.25);
  border-radius: 8px;
  overflow: hidden;
}
.vl-video-head {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  color: #eaf6ff;
  font-size: 13px;
  border-bottom: 1px solid rgba(0, 211, 255, 0.18);
  .name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
  .st {
    margin-left: 8px;
    font-size: 11px;
    flex: none;
    &.on {
      color: #2ce08b;
    }
    &.off {
      color: #ffc53d;
    }
  }
}
.vl-video-switch {
  padding: 2px 8px;
  color: #9fd7ff;
  cursor: pointer;
  user-select: none;
  &:active {
    transform: scale(0.9);
  }
}
.vl-video-box {
  position: relative;
  width: 100%;
  height: clamp(130px, 19vh, 168px);
  background: #000;
  .vl-player {
    width: 100%;
    height: 100%;
  }
  &.clickable {
    cursor: pointer;
  }
}
/* 悬停提示可点击全屏 */
.vl-video-fullhint {
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 3;
  padding: 2px 8px;
  font-size: 12px;
  color: #eaf6ff;
  background: rgba(4, 22, 48, 0.78);
  border: 1px solid rgba(0, 211, 255, 0.45);
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}
.vl-video-box.clickable:hover .vl-video-fullhint {
  opacity: 1;
}
/* 路位换车下拉(摄像头车超过3台才出现) */
.vl-cam-pick {
  flex: 1;
  min-width: 0;
  margin-right: 8px;
  ::v-deep .el-input__inner {
    height: 24px;
    line-height: 24px;
    font-size: 12px;
    color: #eaf6ff;
    background: rgba(10, 40, 78, 0.6);
    border-color: rgba(0, 211, 255, 0.35);
  }
  ::v-deep .el-input__icon {
    line-height: 24px;
  }
}
/* PC 全屏观看一层(盖住顶栏/角标按钮;关闭按钮常驻右上角) */
.vl-fs {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 500;
  display: flex;
  flex-direction: column;
  background: rgba(2, 10, 22, 0.97);
  .vl-fs-head {
    display: flex;
    align-items: center;
    flex: none;
    height: 52px;
    padding: 0 16px;
    background: rgba(6, 27, 56, 0.9);
    border-bottom: 1px solid rgba(0, 211, 255, 0.25);
    .name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: #eaf6ff;
      font-size: 16px;
      font-weight: 600;
    }
    .st {
      margin-right: 14px;
      font-size: 13px;
      &.on {
        color: #2ce08b;
      }
    }
    .vl-fs-close {
      display: flex;
      align-items: center;
      flex: none;
      cursor: pointer;
      color: #eaf6ff;
      font-size: 13px;
      border: 1px solid rgba(0, 211, 255, 0.5);
      border-radius: 4px;
      padding: 6px 14px;
      user-select: none;
      &:hover {
        background: rgba(0, 211, 255, 0.18);
      }
      &:active {
        transform: scale(0.95);
      }
    }
  }
  .vl-fs-box {
    flex: 1;
    min-height: 0;
    padding: 12px;
    .vl-player {
      width: 100%;
      height: 100%;
      background: #000;
    }
  }
}
.vl-video-off {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 14px;
  color: #8fb6d9;
  font-size: 12px;
  text-align: center;
  /* 半透明覆盖:播放器就绪过程中画面出帧即透出,不再整块挡黑 */
  background: rgba(4, 22, 48, 0.55);
  pointer-events: none;
  i {
    font-style: normal;
    color: #6f8dab;
    font-size: 11px;
  }
}
.vl-video-wechat {
  padding: 4px 10px;
  color: #ffc53d;
  font-size: 11px;
  background: rgba(255, 197, 61, 0.08);
}

.vl-topbar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  background: linear-gradient(180deg, rgba(4, 22, 48, 0.92), rgba(4, 22, 48, 0.55));
  border-bottom: 1px solid rgba(0, 211, 255, 0.22);
  z-index: 200;
  /* 行包装器：桌面参与单行布局，手机变真实行 */
  .vl-row {
    display: contents;
  }
  /* 桌面单行顺序：标题 → 考试下拉 → 状态 → 返回 */
  .vl-exam-select {
    order: 2;
    width: 300px;
  }
  /* 移动端顶栏 pill：点开底部弹层(替代PC下拉框,设备多时更好用) */
  .vl-pill {
    display: inline-flex;
    align-items: center;
    max-width: 42vw;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 5px 10px;
    font-size: 12px;
    color: #cfeaff;
    background: rgba(0, 211, 255, 0.1);
    border: 1px solid rgba(0, 211, 255, 0.35);
    border-radius: 14px;
    cursor: pointer;
    &.dim {
      color: #7ea9c9;
    }
  }
  .vl-status {
    order: 3;
    margin-left: auto;
    margin-right: 18px;
    color: #9fd7ff;
    font-size: 13px;
    display: flex;
    align-items: center;
    white-space: nowrap;
  }
  .vl-back {
    order: 4;
    cursor: pointer;
    color: #9fd7ff;
    font-size: 13px;
    border: 1px solid rgba(0, 211, 255, 0.35);
    border-radius: 4px;
    padding: 4px 12px;
    white-space: nowrap;
    &:hover {
      color: #fff;
      border-color: #00d3ff;
    }
  }
  // 本项目无全局 .page-title 样式（天津侧的全站标题样式未随迁），标题字体直接在本组件内定义
  .vl-title-main {
    margin-right: 24px;
    white-space: nowrap;
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 2px;
    line-height: 1.35;
    color: #ffffff;
    text-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
  }
  .vl-title {
    order: 1;
  }
  .live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2ce08b;
    margin-right: 6px;
    &.busy {
      background: #ffc53d;
    }
  }
}

.vl-panel {
  position: absolute;
  top: 72px;
  left: 16px;
  width: 380px;
  max-height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  background: rgba(6, 27, 56, 0.88);
  border: 1px solid rgba(0, 211, 255, 0.25);
  border-radius: 8px;
  z-index: 200;
  .vl-panel-head {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    font-size: 16px;
    font-weight: 600;
    color: #eaf6ff;
    border-bottom: 1px solid rgba(0, 211, 255, 0.18);
    .vl-count {
      font-size: 13px;
      font-weight: 400;
      color: #9fd7ff;
    }
    .vl-arrow {
      display: none;
      margin-left: auto;
      font-size: 12px;
      font-weight: 400;
      color: #9fd7ff;
      transition: transform 0.25s;
      &.open {
        transform: rotate(180deg);
      }
    }
  }
  .vl-panel-tools {
    display: flex;
    gap: 14px;
    padding: 6px 16px;
    border-bottom: 1px solid rgba(0, 211, 255, 0.12);
    .vl-tool {
      font-size: 12px;
      color: #9fd7ff;
      cursor: pointer;
      &:hover {
        color: #eaf6ff;
      }
    }
  }
  .vl-cards {
    flex: 1;
    overflow-y: auto;
    padding: 10px 12px;
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 211, 255, 0.35);
    }
  }
  .vl-group {
    margin-bottom: 6px;
  }
  .vl-group-head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 8px;
    font-size: 13px;
    color: #cfeaff;
    background: rgba(0, 211, 255, 0.07);
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    .vl-arrow-sm {
      font-size: 10px;
      color: #9fd7ff;
      transition: transform 0.2s;
      &.open {
        transform: rotate(90deg);
      }
    }
    .vl-group-name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .vl-group-count {
      font-size: 12px;
      font-weight: 400;
      color: #9fd7ff;
    }
  }
  .vl-card {
    padding: 10px 12px;
    border: 1px solid rgba(0, 211, 255, 0.16);
    border-radius: 6px;
    margin-bottom: 8px;
    cursor: pointer;
    background: rgba(10, 40, 78, 0.5);
    &:hover {
      border-color: rgba(0, 211, 255, 0.55);
    }
    &.active {
      border-color: #00d3ff;
      background: rgba(0, 90, 150, 0.45);
    }
    &.offline {
      opacity: 0.62;
    }
    .vl-card-top {
      display: flex;
      align-items: center;
      .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 8px;
        flex: none;
        &.on {
          background: #2ce08b;
          box-shadow: 0 0 6px #2ce08b;
        }
        &.off {
          background: #7b8aa0;
        }
      }
      .name {
        flex: 1;
        color: #eaf6ff;
        font-size: 14px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .speed {
        color: #9fd7ff;
        font-size: 12px;
        &.moving {
          color: #ffc53d;
        }
      }
    }
    .vl-card-sub {
      margin-top: 6px;
      color: #8fb6d9;
      font-size: 12px;
      padding-left: 16px;
    }
  }
  .vl-empty {
    text-align: center;
    color: #8fb6d9;
    padding: 30px 0;
    font-size: 13px;
  }
  .vl-panel-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    border-top: 1px solid rgba(0, 211, 255, 0.18);
    ::v-deep .el-checkbox__label {
      color: #9fd7ff;
      font-size: 12px;
    }
    .vl-fitview {
      color: #00d3ff;
    }
  }
}

/* ---- 移动端底部弹层(筛选/搜索/摄像头选车),z-index 高于视频面板(500) ---- */
.vl-sheet-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
  display: flex;
  align-items: flex-end;
}
.vl-sheet {
  width: 100%;
  max-height: 72vh;
  display: flex;
  flex-direction: column;
  background: #0a1c33;
  border: 1px solid rgba(0, 211, 255, 0.3);
  border-bottom: none;
  border-radius: 14px 14px 0 0;
  .vl-sheet-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    font-size: 15px;
    font-weight: 600;
    color: #eaf6ff;
    border-bottom: 1px solid rgba(0, 211, 255, 0.18);
    .vl-sheet-close {
      font-size: 16px;
      font-weight: 400;
      color: #9fd7ff;
      cursor: pointer;
      padding: 2px 6px;
    }
  }
  .vl-sheet-search {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    input {
      flex: 1;
      box-sizing: border-box;
      padding: 9px 12px;
      font-size: 14px;
      color: #eaf6ff;
      background: rgba(255, 255, 255, 0.07);
      border: 1px solid rgba(0, 211, 255, 0.3);
      border-radius: 8px;
      outline: none;
      &::placeholder {
        color: #6f93b3;
      }
    }
    .vl-sheet-clear {
      flex: none;
      font-size: 12px;
      color: #9fd7ff;
      cursor: pointer;
    }
  }
  .vl-sheet-list {
    flex: 1;
    overflow-y: auto;
    padding: 6px 10px 18px;
  }
  .vl-sheet-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 10px;
    font-size: 14px;
    color: #cfeaff;
    border-radius: 8px;
    cursor: pointer;
    &.active {
      background: rgba(0, 211, 255, 0.16);
      color: #eaf6ff;
    }
    .name {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .tag {
      flex: none;
      max-width: 40%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
      color: #9fd7ff;
    }
  }
  .vl-sheet-empty {
    padding: 30px 0;
    text-align: center;
    font-size: 13px;
    color: #6f93b3;
  }
}

/* 移动端摄像头面板标题:点开选车弹层 */
.vl-video-single .vl-video-head .name.pick {
  cursor: pointer;
  .pick-mark {
    font-size: 11px;
    color: #9fd7ff;
  }
}

/* ---- 中窄屏(平板横竖屏)：压缩顶栏字号与下拉宽度 ---- */
@media (min-width: 769px) and (max-width: 1100px) {
  .vl-topbar {
    .vl-title-main {
      font-size: 15px;
      letter-spacing: 1px;
      margin-right: 12px;
    }
    .vl-exam-select {
      width: 200px;
    }
    .vl-status {
      margin-right: 10px;
      font-size: 12px;
    }
  }
}

/* ---- 手机/平板适配：顶栏两行 + 车辆列表变底部抽屉 ---- */
@media (max-width: 768px) {
  .vl-topbar {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 8px 12px;
    row-gap: 6px;
    .vl-row {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }
    .vl-row.row-title {
      justify-content: space-between;
    }
    .vl-title {
      flex: 1 1 0;
      min-width: 0;
      overflow: hidden;
    }
    .vl-title-main {
      display: block;
      font-size: 15px;
      letter-spacing: 1px;
      margin-right: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .vl-back {
      flex: none;
      padding: 3px 10px;
    }
    .vl-row.row-data .vl-pill {
      flex: 0 1 auto;
      min-width: 0;
    }
    .vl-row.row-data .vl-status {
      margin-left: auto;
    }
    .vl-status {
      order: 0;
      flex: none;
      margin: 0;
      font-size: 12px;
    }
  }
  .vl-mode-btn {
    bottom: calc(env(safe-area-inset-bottom) + 58px);
  }
  .vl-cars-btn {
    display: flex;
    bottom: calc(env(safe-area-inset-bottom) + 104px);
  }
  .vl-cam-toggle {
    order: 3; /* 手机标题行:插到标题与返回之间 */
  }
  .vl-videos {
    display: none;
  }
  .vl-video-single {
    position: fixed;
    top: calc(env(safe-area-inset-top) + 96px);
    left: 10px;
    right: 10px;
    z-index: 250;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
    .vl-video-box {
      height: 230px;
    }
  }
  .vl-panel {
    /* fixed 而非 absolute:手机浏览器 100vh 含地址栏后的区域,absolute 会把面板顶出可视区 */
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    display: none;
    max-height: 45vh;
    padding-bottom: env(safe-area-inset-bottom);
    border-radius: 12px 12px 0 0;
    overflow: hidden;
    &.open {
      display: flex;
    }
    .vl-panel-head {
      cursor: pointer;
      .vl-arrow {
        display: inline-block;
      }
    }
  }
}
</style>

<style lang="scss">
/* 摄像头路位换车下拉面板:popper 挂在 body 上,须全局样式;设备名过长省略 */
.el-select-dropdown.vl-cam-pick-popper {
  max-width: 420px;
  .el-select-dropdown__item.selected {
    color: #00a6e0;
  }
}

/* 车辆 marker 与信息窗体（全局，供 AMap content 使用） */
.vl-marker {
  position: relative;
  img {
    /* 原图 24×48(1:2 竖长俯视车),等比缩放,强制方形会把车压扁 */
    width: 22px;
    height: 44px;
    display: block;
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.6));
    transition: transform 0.6s;
  }
  .vl-marker-label {
    position: absolute;
    left: 50%;
    bottom: -16px;
    transform: translateX(-50%);
    white-space: nowrap;
    font-size: 12px;
    color: #eaf6ff;
    background: rgba(6, 27, 56, 0.85);
    border: 1px solid rgba(0, 211, 255, 0.4);
    border-radius: 3px;
    padding: 0 6px;
    line-height: 18px;
  }
  &.offline img {
    animation: none;
  }
}
.vl-infowin {
  background: rgba(6, 27, 56, 0.95);
  border: 1px solid rgba(0, 211, 255, 0.45);
  border-radius: 6px;
  padding: 10px 14px;
  min-width: 220px;
  .vl-infowin-title {
    color: #eaf6ff;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 6px;
    border-bottom: 1px solid rgba(0, 211, 255, 0.25);
    padding-bottom: 6px;
  }
  .vl-infowin-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    line-height: 22px;
    span {
      color: #8fb6d9;
      margin-right: 18px;
    }
    b {
      color: #eaf6ff;
      font-weight: 500;
    }
  }
}
</style>
